import { DataGrid, Skeleton, Tooltip } from '@ghs/components';
import { IconButton, Stack, StackProps, Typography } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid-pro';
import { useUnit } from 'effector-react';
import { AcsEvent } from '../../services/SplService';
import { formatAddressMultipleLines } from '../../util/AddressUtils';
import { $$splResultsPage } from './model';
import * as React from 'react';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

type AcsEventsProps = StackProps;

export default function AcsEvents(props: AcsEventsProps) {
  const { ...overrides } = props;

  const mailPiece = useUnit($$splResultsPage.$mailPiece);
  const data = useUnit($$splResultsPage.$acsEvents);
  const isLoading = useUnit($$splResultsPage.$isLoading);

  if (isLoading) {
    return (
      <Stack spacing={1}>
        <Skeleton variant="rectangular" />
        <Skeleton variant="rectangular" />
        <Skeleton variant="rectangular" />
      </Stack>
    );
  }

  if (!mailPiece || !data.length) {
    return null;
  }

  return (
    <Stack {...overrides} data-testid="acs-events-container" spacing={2}>
      <Typography variant="h5" color="text.primary">
        ACS Events
      </Typography>
      <DataGrid
        columns={columns}
        rows={data}
        rowSelection={false}
        getRowHeight={() => 'auto'}
        // Restore padding due to using getRowHeight & disable cell selection outline
        sx={{
          '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
          '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
          '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' },
          '& .MuiDataGrid-cell:focus': { outline: 'none' },
          '& .MuiDataGrid-cell:focus-within': { outline: 'none' }
        }}
      />
    </Stack>
  );
}

const columns: GridColDef<AcsEvent>[] = [
  { field: 'dateReceived', headerName: 'ACS Event Received', flex: 0.5 },
  { field: 'reason', headerName: 'Reason', flex: 0.5 },
  { field: 'uspsDeliverabilityCode', headerName: 'Deliverability Code', flex: 0.5 },
  {
    field: 'newAddress',
    headerName: 'USPS Supplied New Address',
    valueGetter: (_, row) => formatAddressMultipleLines(null, row.address1, row.address2, row.address3, row.cityStateZip),
    renderCell: params => {
      return <div style={{ whiteSpace: 'pre-wrap' }}>{params.value}</div>;
    },
    flex: 1
  },
  {
    field: 'actions',
    type: 'actions',
    headerName: '',
    headerAlign: 'center',
    getActions: params =>
      params.row.eventDetails
        ? [
            <Tooltip title="More Information" placement="top" key={`row-${params.id}`}>
              <IconButton
                data-testid="acs-event-details-button"
                onClick={() => {
                  $$splResultsPage.acsEventDialogOpen(params.row.id);
                }}
              >
                <InfoOutlinedIcon sx={{ color: 'text.primary' }} />
              </IconButton>
            </Tooltip>
          ]
        : []
  }
];
